import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ru } from 'date-fns/locale/ru';
import { isValid } from 'date-fns/isValid';
import { format, parse } from 'date-fns';
import DatePicker, { registerLocale } from 'react-datepicker';
import pick from 'lodash/pick';

import 'react-datepicker/dist/react-datepicker.css';
import './DateTimeInput.scss';
import SimpleMaskInput from '../SimpleMaskInput';
import type { SimpleStringInputProps } from '../SimpleStringInput';

import { inputPropTypes } from '../utils';

registerLocale('ru', ru);

/**
 * «Глупый» инпут времени (без Formik): DatePicker в режиме только времени
 * поверх SimpleMaskInput с маской 00:00. Formik-обёртка — TimeInput.
 *
 * `pick(rest, Object.keys(inputPropTypes))` — runtime-фильтр (см. SimpleStringInput):
 * до DatePicker доходят только whitelisted-пропсы, «мусорные» — отсеиваются.
 */
export interface SimpleTimeInputProps extends Omit<SimpleStringInputProps, 'value' | 'onChange'> {
  /** Формат времени. @default 'HH:mm' */
  displayFormat?: string;
  /** Значение времени строкой в displayFormat. */
  value?: string;
  /**
   * (event, newValue, isValid) — при выборе из списка и при полном ручном вводе.
   * Дефолт `Function` сохранён из .jsx-версии 1:1 (глобальный конструктор —
   * стабильная ссылка); фактически все текущие потребители передают свой onChange.
   */
  onChange?: (...args: unknown[]) => unknown;
  /** Шаг минут в выпадающем списке. @default 30 */
  timeIntervals?: number;
  /** Показывать крестик очистки DatePicker. @default false */
  isClearable?: boolean;
  /** @default true */
  showTimeSelect?: boolean;
}

// const SimpleTimeInput = forwardRef<HTMLElement, SimpleTimeInputProps>((props, ref) => {
const SimpleTimeInput = forwardRef<any, SimpleTimeInputProps>((props, ref) => {
  const picker = useRef<any>(null);
  const {
    displayFormat = 'HH:mm',
    value,
    onChange = Function as unknown as (...args: unknown[]) => unknown,
    error,
    timeIntervals = 30,
    isClearable = false,
    showTimeSelect = true,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(value);

  useImperativeHandle(ref, () => picker.current);

  const input = (
    <DatePicker
      { ...pick(rest, Object.keys(inputPropTypes)) }
      className='input-group form-control'
      locale='ru'
      ref={ picker }
      selected={ inputValue
          && isValid(parse(inputValue,
            displayFormat, new Date())) ? parse(inputValue, displayFormat, new Date()) : null }
      onChange={ (v: any, e) => {
        // Выбор из выпадающего списка — v это валидный Date
        if (v && isValid(v)) {
          const newValue = format(v, displayFormat);
          setInputValue(newValue);
          if (onChange) { onChange(e || { target: { value: newValue } }, newValue, true); }
        }
      } }
      showTimeSelect={ showTimeSelect }
      showTimeSelectOnly
      timeIntervals={ timeIntervals }
      timeCaption='Время'
      dateFormat={ displayFormat }
      timeFormat={ displayFormat }
      isClearable={ isClearable }
      value={ inputValue }
      customInput={ (
        <SimpleMaskInput
          maskPattern='00:00'
          // Пустышка onChange — блокируем вызов onChange DatePicker от SimpleMaskInput
          onChange={ () => {} }
          onManualChange={ (maskedValue) => {
            setInputValue(maskedValue);

            const isComplete = maskedValue?.length === displayFormat.length;
            const valid = isComplete && isValid(parse(maskedValue, displayFormat, new Date()));
            if (onChange) { onChange({ target: { value: maskedValue } }, maskedValue, valid); }
          } }
          error={ error }
        />
      ) }
      { ...(rest as any) } /* DatePickerProps v8 — юнион (selectsRange|selectsMultiple);
                                     спред широкого rest на юнион не тайпчекается, runtime 1:1 */
    />
  );

  return (
    <> { input } </>
  );
});

SimpleTimeInput.displayName = 'SimpleTimeInput';

export default SimpleTimeInput;
